import db from '../services/db';
import { extractMediaStoragePath } from './writingCheckerImages.js';
import { isSignedStorageUrlValid } from './signedMediaUrlCache.js';

const SRC_ATTR_RE = /\bsrc=(["'])(.*?)\1/gi;
const DATA_AUDIO_SRC_RE = /\bdata-audio-src=(["'])(.*?)\1/gi;

/** Collect unique media-bucket URLs from HTML attributes. */
export function collectMediaSrcUrls(html) {
    if (!html || typeof html !== 'string') return [];
    const urls = new Set();

    for (const re of [SRC_ATTR_RE, DATA_AUDIO_SRC_RE]) {
        const pattern = new RegExp(re.source, 'gi');
        let match;
        while ((match = pattern.exec(html)) !== null) {
            const url = match[2];
            if (url && extractMediaStoragePath(url)) urls.add(url);
        }
    }

    return [...urls];
}

/** True if any embedded storage URL needs a fresh signed token. */
export function needsMediaUrlRefresh(html) {
    const urls = collectMediaSrcUrls(html);
    return urls.some((url) => !isSignedStorageUrlValid(url));
}

/** Replace embedded storage URLs with fresh signed URLs (one sign request per object path). */
export async function resolveMediaUrlsInHtml(html) {
    if (!html) return html;
    const urls = collectMediaSrcUrls(html);
    if (urls.length === 0) return html;

    const pathToUrls = new Map();
    for (const url of urls) {
        const path = extractMediaStoragePath(url);
        if (!path) continue;
        if (!pathToUrls.has(path)) pathToUrls.set(path, []);
        pathToUrls.get(path).push(url);
    }

    if (pathToUrls.size === 0) return html;

    const pathsToSign = [];
    for (const [path, variants] of pathToUrls.entries()) {
        if (!variants.some((url) => isSignedStorageUrlValid(url))) {
            pathsToSign.push([path, variants]);
        }
    }

    if (pathsToSign.length === 0) return html;

    const signedByPath = new Map();
    const pathReplacements = await Promise.all(
        pathsToSign.map(async ([path, variants]) => {
            try {
                const signed = await db.getSignedMediaUrl(variants[0]);
                return [path, signed || variants[0]];
            } catch {
                return [path, variants[0]];
            }
        })
    );

    for (const [path, signed] of pathReplacements) {
        signedByPath.set(path, signed);
    }

    let out = html;
    for (const [path, variants] of pathToUrls.entries()) {
        const signed = signedByPath.get(path);
        if (!signed) continue;
        for (const from of variants) {
            if (from !== signed) out = out.split(from).join(signed);
        }
    }

    return out;
}
